import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Bot,
  Copy,
  Edit2,
  Plus,
  Trash2,
  TriangleAlert,
  Webhook,
} from "lucide-react";
import { useAutomationsApi } from "../api/automationsApi";
import AutomationModal from "../modal/AutomationModal";
import ConfirmModal from "../modal/ConfirmModal";

const TABS = [
  { value: "rules", label: "Automações" },
  { value: "runs", label: "Execuções" },
  { value: "webhooks", label: "Webhooks de entrada" },
];

const STATUS_BADGE = {
  SUCCESS: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  SKIPPED: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  FAILED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  REJECTED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  PROCESSING: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

const Automations = () => {
  const api = useAutomationsApi();
  const [tab, setTab] = useState("rules");
  const [catalog, setCatalog] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [runs, setRuns] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [newEndpointName, setNewEndpointName] = useState("");
  const [createdEndpoint, setCreatedEndpoint] = useState(null);

  const load = useCallback(async () => {
    try {
      const [catalogData, list] = await Promise.all([api.catalog(), api.getAll()]);
      setCatalog(catalogData);
      setAutomations(list);
    } catch (error) {
      toast.error(error.message || "Não foi possível carregar as automações.");
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === "runs") api.runs().then(setRuns).catch(() => {});
    if (tab === "webhooks") {
      api.listEndpoints().then(setEndpoints).catch(() => {});
      api.deliveries().then(setDeliveries).catch(() => {});
    }
  }, [tab, api]);

  const toggle = async (automation) => {
    try {
      await api.setEnabled(automation.id, !automation.enabled, true);
      await load();
      toast.success(automation.enabled ? "Automação pausada." : "Automação ativada.");
    } catch (error) {
      toast.error(error.message || "Não foi possível alterar.");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.remove(toDelete);
      setToDelete(null);
      await load();
      toast.success("Automação excluída.");
    } catch (error) {
      toast.error(error.message || "Não foi possível excluir.");
    }
  };

  const createEndpoint = async () => {
    if (!newEndpointName.trim()) return;
    try {
      const endpoint = await api.createEndpoint({
        name: newEndpointName,
        allowedActions: ["send_message", "move_stage", "add_tag", "set_status", "notify_team"],
      });
      setCreatedEndpoint(endpoint);
      setNewEndpointName("");
      setEndpoints(await api.listEndpoints());
    } catch (error) {
      toast.error(error.message || "Não foi possível criar o endpoint.");
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copiado.");
  };

  const baseUrl = `${process.env.REACT_APP_API_URL || ""}/webhooks/in`;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Automações</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Regras que agem sozinhas no funil e no atendimento, com teto diário e
              registro de tudo que foi executado.
            </p>
          </div>

          {tab === "rules" && (
            <button
              onClick={() => { setEditing(null); setModalOpen(true); }}
              className="flex items-center gap-1.5 rounded-lg bg-green-500 px-4 py-2 text-sm text-white hover:bg-green-600"
            >
              <Plus className="h-4 w-4" />
              Nova automação
            </button>
          )}
        </div>

        <div className="mb-4 flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
          {TABS.map((option) => (
            <button
              key={option.value}
              onClick={() => setTab(option.value)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                tab === option.value
                  ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
                  : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {tab === "rules" && (
          <div className="space-y-3">
            {automations.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
                <Bot className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                <p className="text-gray-600 dark:text-gray-400">
                  Nenhuma automação ainda. Comece por uma interna, sem envio ao cliente.
                </p>
              </div>
            )}

            {automations.map((automation) => (
              <div
                key={automation.id}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{automation.name}</p>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        automation.enabled
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                      }`}>
                        {automation.enabled ? "Ativa" : "Pausada"}
                      </span>
                      {automation.external && (
                        <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          <TriangleAlert className="h-3 w-3" />
                          Age fora do CRM
                        </span>
                      )}
                    </div>

                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {catalog?.triggers?.[automation.trigger]?.label || automation.trigger}
                      {" → "}
                      {(automation.actions || [])
                        .map((action) => catalog?.actions?.[action.type]?.label || action.type)
                        .join(", ")}
                      {" · teto "}{automation.dailyLimit}/dia
                      {automation.last24h?.success ? ` · ${automation.last24h.success} execuções em 24h` : ""}
                      {automation.last24h?.failed ? ` · ${automation.last24h.failed} falhas` : ""}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggle(automation)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        automation.enabled ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"
                      }`}
                      role="switch"
                      aria-checked={automation.enabled}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        automation.enabled ? "translate-x-6" : "translate-x-1"
                      }`} />
                    </button>

                    <button
                      onClick={() => { setEditing(automation); setModalOpen(true); }}
                      className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setToDelete(automation.id)}
                      className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "runs" && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {["Quando", "Automação", "Gatilho", "Resultado", "Detalhe"].map((header) => (
                    <th key={header} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-600 dark:text-gray-400">
                      {new Date(run.createdAt).toLocaleString("pt-BR")}
                    </td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{run.automation?.name}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{run.trigger}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[run.status] || ""}`}>
                        {run.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                      {run.error || run.detail?.reason || ""}
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-500">Nada executado ainda.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "webhooks" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                Novo endpoint de entrada
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  value={newEndpointName}
                  onChange={(event) => setNewEndpointName(event.target.value)}
                  placeholder="Nome do integrador (ex.: Site, n8n)"
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
                <button onClick={createEndpoint} className="flex items-center gap-1 rounded-lg bg-green-500 px-4 py-2 text-sm text-white hover:bg-green-600">
                  <Webhook className="h-4 w-4" />
                  Gerar
                </button>
              </div>

              {createdEndpoint && (
                <div className="mt-3 space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    Guarde o segredo agora — ele não é exibido de novo.
                  </p>
                  {[
                    ["URL", `${baseUrl}/${createdEndpoint.token}`],
                    ["Segredo (HMAC)", createdEndpoint.secret],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-center gap-2">
                      <span className="w-32 shrink-0 text-xs text-amber-800 dark:text-amber-300">{label}</span>
                      <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 text-xs dark:bg-gray-800 dark:text-gray-200">{value}</code>
                      <button onClick={() => copy(value)} className="rounded p-1 text-amber-700 hover:bg-amber-100 dark:text-amber-300">
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {["Nome", "Ativo", "Último uso", "Ações permitidas", ""].map((header) => (
                      <th key={header} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {endpoints.map((endpoint) => (
                    <tr key={endpoint.id}>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{endpoint.name}</td>
                      <td className="px-4 py-2">
                        <button
                          onClick={async () => {
                            await api.setEndpointEnabled(endpoint.id, !endpoint.enabled);
                            setEndpoints(await api.listEndpoints());
                          }}
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            endpoint.enabled
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                          }`}
                        >
                          {endpoint.enabled ? "Ativo" : "Desativado"}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                        {endpoint.lastUsedAt ? new Date(endpoint.lastUsedAt).toLocaleString("pt-BR") : "nunca"}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                        {(endpoint.allowedActions || []).join(", ")}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={async () => {
                            await api.removeEndpoint(endpoint.id);
                            setEndpoints(await api.listEndpoints());
                          }}
                          className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {endpoints.length === 0 && (
                    <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-500">Nenhum endpoint criado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              <p className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100">
                Chamadas recebidas
              </p>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {deliveries.map((delivery) => (
                    <tr key={delivery.id}>
                      <td className="whitespace-nowrap px-4 py-2 text-gray-600 dark:text-gray-400">
                        {new Date(delivery.createdAt).toLocaleString("pt-BR")}
                      </td>
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{delivery.action || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[delivery.status] || ""}`}>
                          {delivery.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">{delivery.error || ""}</td>
                    </tr>
                  ))}
                  {deliveries.length === 0 && (
                    <tr><td colSpan="4" className="px-4 py-6 text-center text-gray-500">Nenhuma chamada recebida.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <AutomationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        automation={editing}
        catalog={catalog}
      />

      <ConfirmModal
        isOpen={Boolean(toDelete)}
        title="Excluir automação"
        message="A automação e o histórico de execuções dela serão removidos."
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
};

export default Automations;
